import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import buttonImage from '../buttonimage.webp';
import backgroundImage from '../background.webp';
import cardBackgroundImage from '../cardbackground.webp';

const positionTitles = [
    'What you want most right now',
    'An underlying issue',
    'What are you releasing?',
    'What can you surrender?',
    'Your fear',
    'Too controlling with'
];

const conceptsCardBack = 'https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/card-images/Concepts/cbackimage.webp';

function Resistance({ goBack, goHome, handleSaveProgress }) {
    const [conceptsCards, setConceptsCards] = useState([]);
    const [shuffledConceptsCards, setShuffledConceptsCards] = useState([]);
    const [selectedConceptsCards, setSelectedConceptsCards] = useState([]);
    const [showConceptsPicker, setShowConceptsPicker] = useState(false);

    useEffect(() => {
        async function fetchConceptsCards() {
            const { data, error } = await supabase
                .schema('public')
                .from('cards')
                .select('id, title, image_path, descriptions, card_number')
                .eq('component', 'Concepts');
            if (!error && data && data.length > 0) {
                setConceptsCards(data);
                setShuffledConceptsCards([...data]);
            }
        }
        fetchConceptsCards();
    }, []);

    function shuffleArray(array) {
        const arr = [...array];
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    }

    const handleStartConceptsReading = () => {
        setShowConceptsPicker(true);
        setSelectedConceptsCards([]);
        setShuffledConceptsCards(shuffleArray(conceptsCards));
    };

    const handleHideConceptsPicker = () => {
        setShowConceptsPicker(false);
    };

    const handleConceptsCardClick = (card, idx) => {
        if (selectedConceptsCards.length < 6 && !selectedConceptsCards.some(c => c.id === card.id)) {
            setSelectedConceptsCards([...selectedConceptsCards, { ...card, position: idx }]);
        }
    };

    const handleSave = async () => {
        if (typeof handleSaveProgress === 'function') {
            await handleSaveProgress('resistance', null, false);
            alert('Session progress has been saved successfully!');
        }
    };

    return (
        <>
            <style>
                {`
                @media (max-width: 600px) {
                    .resistance-btn-row {
                        flex-direction: row !important;
                        gap: 6px !important;
                    }
                    .resistance-btn {
                        width: 32vw !important;
                        min-width: 0 !important;
                        max-width: 100px !important;
                        padding: 10px 0 !important;
                        font-size: 16px !important;
                    }
                }
                `}
            </style>
            <div style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '16px',
            }}>
                {/* Title */}
                <h3 style={{
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: 'black',
                    marginBottom: '32px',
                }}>
                    Resistance
                </h3>

                {/* Session Helper Cards Row */}
                <div
                    className="resistance-session-helper-row"
                    style={{
                        display: 'flex',
                        flexDirection: window.innerWidth <= 600 ? 'column' : 'row',
                        gap: '16px',
                        width: '100%',
                        maxWidth: '600px',
                        marginBottom: '16px',
                    }}
                >
                    {/* Left Card */}
                    <div style={{
                        flex: 1,
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'left',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        marginBottom: window.innerWidth <= 600 ? '16px' : 0,
                    }}>
                        <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                        <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                            <div>• Resistance can be hidden in our subconscious beliefs.</div>
                            <div>• When we resist something it makes it persist in our experience.</div>
                            <div>• Integration is resolution, whereas resistance loops the energy.</div>
                            <div>• It creates a feedback loop that distorts perception and limits growth, as the energy tied up in resistance clouds clarity and blocks new possibilities.</div>
                            <div>• Integration, rather than resistance, allows energy to flow freely, fostering wellness and alignment by embracing all parts of an experience.</div>
                            <div>• Resistance amplifies inner conflict by channeling energy into avoidance or defense, strengthening the very patterns we resist, like a dam building pressure in the mind and body</div>
                        </div>
                    </div>
                    {/* Right Card */}
                    <div style={{
                        flex: 1,
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'left',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}>
                        <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                        <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                            <div>1. Have you accepted this, even if you don't like it?</div>
                            <div>2. How has resistance shown up in your life?</div>
                            <div>3.  What fear or belief is anchoring your resistance to moving forward with this issue?</div>
                            <div>4. How does holding onto this resistance serve you, and what would happen if you let it go?</div>
                            <div>5. What specific thought or habit keeps you stuck when you try to address this topic?</div>
                            <div>6. If you fully released this resistance today, what truth about yourself would you have to face?</div>
                        </div>
                    </div>
                </div>
                {/* 3 Vertically Stacked Cards with Sample Text */}
                <div style={{ width: '100%', maxWidth: '600px', margin: '0 auto 16px auto' }}>
                    <div style={{
                        border: '2px solid black',
                        borderRadius: '10px',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        marginBottom: '16px',
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <img
                            src="/images/example%20meme.webp"
                            alt="Example Meme"
                            style={{
                                width: '100%',
                                maxWidth: '360px',
                                height: 'auto',
                                marginBottom: '12px',
                                borderRadius: '8px',
                                border: '1px solid #4B5563',
                                objectFit: 'contain',
                                display: 'block',
                                marginLeft: 'auto',
                                marginRight: 'auto'
                            }}
                        />
                    </div>
                    <div style={{
                        border: '2px solid black',
                        borderRadius: '10px',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        marginBottom: '16px',
                        padding: '20px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: 'black',
                        textAlign: 'center',
                    }}>
                        Whenever we are resisting something the energy builds up pressure as it aims to achieve balance and flow. This pressure feels uncomfortable to us.  The degree of discomfort depends on the person and the situation as well as the story that is being told about it.  Based on this I learned to integrate everything instead of resist.  This means that whenever I face something I do not like or want I recognize that it already exists and I accept that.  I then set out to become the version of myself that can handle and process what already exists.  I know that when I want to change something becuase I don't agree with it or I don't like it that I need to create something new to replace it instead of trying to resist or fight against it. <br /> Force is not really my friend.  I discovered that energy is gentle and flowing and it likes to move freely, so I make sure that I too am gentle and flowing enough to allow the energy of anything to pass through me.  Part of this has to do with the fact that I believe I am strong and healthy and capable enough to do this. <br /> We will experience immediate relief whenever we find ourselves resisting and then switch to acceptance. 
                    </div>
                    <div style={{
                        border: '2px solid black',
                        borderRadius: '10px',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        marginBottom: '16px',
                        padding: '20px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: 'black',
                        textAlign: 'center',
                    }}>
                        The Oracle included below has the Concepts card deck, which just has about 450 concepts that may help you relfect on whether you are in resistance on that topic or not.  It is possible to set the intention that the cards given will pertain to your situation and help you gain insight on what you have in front of you and how to navigate it.
                    </div>
                </div>

                {/* Concepts Card Picker Display */}
                {showConceptsPicker && shuffledConceptsCards.length > 0 && (
                    <div
                        style={{
                            width: window.innerWidth <= 768 ? '100vw' : '1000px',
                            maxWidth: window.innerWidth <= 768 ? '100vw' : '1000px',
                            margin: '0 auto',
                            padding: '10px 0',
                            background: 'white',
                            border: '2px solid #4B5563',
                            borderRadius: '8px',
                            zIndex: 1,
                            overflow: 'hidden',
                            position: 'relative',
                            boxShadow: 'none',
                        }}
                    >
                        <div style={{
                            display: 'block',
                            verticalAlign: 'top',
                            position: 'relative',
                            padding: '0',
                            textAlign: 'left',
                            overflow: 'visible',
                            background: 'transparent',
                            border: 'none',
                            boxShadow: 'none',
                            marginLeft: '6px',
                            marginRight: '6px',
                        }}>
                            {(() => {
                                const cards = shuffledConceptsCards.filter(card =>
                                    !card.image_path?.toLowerCase().includes('cbackimage.webp')
                                );
                                const totalCards = cards.length;
                                const cardWidth = 150;
                                const cardHeight = 150;
                                const margin = 6;
                                // Use 10 rows for mobile, 5 rows for desktop
                                const numRows = window.innerWidth <= 768 ? 10 : 5;
                                const rowGap = 8;
                                const containerWidth = (window.innerWidth <= 768 ? window.innerWidth : 1000) - 2 * margin;
                                const cardsPerRow = Math.ceil(totalCards / numRows);
                                const overlapPx = cardsPerRow > 1
                                    ? Math.floor((cardWidth * cardsPerRow - containerWidth) / (cardsPerRow - 1))
                                    : 0;
                                const effectiveOverlap = overlapPx > 0 ? overlapPx : 0;
                                // Dynamically create rows
                                const rows = [];
                                for (let i = 0; i < totalCards; i++) {
                                    const rowIdx = Math.floor(i / cardsPerRow);
                                    if (!rows[rowIdx]) rows[rowIdx] = [];
                                    rows[rowIdx].push(cards[i]);
                                }
                                const containerHeight = numRows * cardHeight + (numRows - 1) * rowGap;
                                return (
                                    <div
                                        style={{
                                            width: `${containerWidth}px`,
                                            minWidth: '300px',
                                            position: 'relative',
                                            height: `${containerHeight}px`,
                                            background: 'transparent',
                                            border: 'none',
                                            boxShadow: 'none',
                                        }}
                                    >
                                        {rows.map((rowCards, rowIdx) => (
                                            <div
                                                key={`concepts-row-${rowIdx}`}
                                                style={{
                                                    position: 'absolute',
                                                    top: `${rowIdx * (cardHeight + rowGap)}px`,
                                                    left: 0,
                                                    height: `${cardHeight}px`,
                                                    width: `${containerWidth}px`,
                                                    minWidth: '300px',
                                                    overflowX: 'visible',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    boxShadow: 'none',
                                                    marginBottom: rowIdx < numRows - 1 ? `${rowGap}px` : '0',
                                                }}
                                            >
                                                {rowCards.map((card, idx) => {
                                                    const left = idx * (cardWidth - effectiveOverlap);
                                                    const maxLeft = containerWidth - cardWidth;
                                                    const adjustedLeft = Math.min(left, maxLeft);
                                                    const zIndex = idx;
                                                    const key = card?.id ? `concepts-row-${rowIdx}-${card.id}` : `concepts-row-${rowIdx}-${idx}`;
                                                    return (
                                                        <div
                                                            key={key}
                                                            data-position={idx}
                                                            style={{
                                                                width: `${cardWidth}px`,
                                                                height: `${cardHeight}px`,
                                                                position: 'absolute',
                                                                left: `${adjustedLeft}px`,
                                                                top: '0px',
                                                                cursor: 'pointer',
                                                                borderRadius: '4px',
                                                                boxShadow: 'none',
                                                                zIndex: zIndex,
                                                                transition: 'transform 0.3s, opacity 0.3s',
                                                                background: 'transparent',
                                                                border: 'none',
                                                                ...(idx === rowCards.length - 1 ? { zIndex: 100 } : {}),
                                                                opacity: selectedConceptsCards.some(c => c.id === card.id) ? 0.5 : 1,
                                                            }}
                                                            onClick={() => handleConceptsCardClick(card, idx)}
                                                        >
                                                            <img
                                                                src={conceptsCardBack}
                                                                alt=""
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'contain',
                                                                    borderRadius: '4px',
                                                                    pointerEvents: 'none',
                                                                    userSelect: 'none',
                                                                    display: 'block',
                                                                    background: 'transparent',
                                                                    border: 'none',
                                                                    boxShadow: 'none',
                                                                }}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                )}

                {/* Concepts Reading Display */}
                {selectedConceptsCards.length > 0 && (
                    <div
                        style={{
                            width: '100%',
                            maxWidth: window.innerWidth <= 768 ? '400px' : '1000px',
                            margin: '0 auto 32px auto',
                            background: 'white',
                            border: '2px solid black',
                            padding: '12px',
                            borderRadius: '8px',
                            overflowX: 'auto',
                            zIndex: 1,
                            minHeight: '320px',
                            minWidth: window.innerWidth <= 768 ? '300px' : '600px',
                        }}
                    >
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gridTemplateRows: 'repeat(2, 1fr)',
                            gap: '8px',
                            ...(window.innerWidth <= 768 && {
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gridTemplateRows: 'repeat(3, 1fr)',
                            }),
                        }}>
                            {selectedConceptsCards
                                .filter(card =>
                                    !card.image_path?.toLowerCase().includes('cbackimage.webp')
                                )
                                .slice(0, 6)
                                .map((card, idx) => (
                                    <div key={card.id} style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        boxShadow: '0 4px 6px rgba(0,0,0,0.3), 0 -4px 6px rgba(0,0,0,0.3)',
                                        minHeight: '200px',
                                    }}>
                                        <p style={{
                                            fontFamily: 'Times New Roman, Times, serif',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            margin: '4px 0',
                                            textAlign: 'center',
                                        }}>
                                            {positionTitles[idx] || `Concept ${idx + 1}`}
                                        </p>
                                        <img
                                            src={
                                                card.image_path
                                                    ? `https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/${card.image_path.replace('card-images/concepts/', 'card-images/Concepts/')}`
                                                    : conceptsCardBack
                                            }
                                            alt={card.title}
                                            style={{ width: '150px', height: '150px', objectFit: 'contain', transition: 'transform 0.2s' }}
                                        />
                                        <p style={{
                                            fontFamily: 'Times New Roman, Times, serif',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            marginTop: '8px',
                                            textAlign: 'center',
                                            wordWrap: 'break-word',
                                            maxWidth: '150px',
                                        }}>
                                            {card && typeof card.descriptions === 'string' && card.descriptions.trim()
                                                ? card.descriptions
                                                : 'No guidance available'}
                                        </p>
                                    </div>
                                ))}
                            {/* Fill empty slots if less than 6 cards picked */}
                            {Array.from({ length: 6 - selectedConceptsCards.filter(card =>
                                !card.image_path?.toLowerCase().includes('cbackimage.webp')
                            ).length }).map((_, idx) => (
                                <div key={`empty-concepts-${idx}`} style={{
                                    width: '150px',
                                    height: '200px',
                                    backgroundColor: '#e0e0e0',
                                    borderRadius: '8px',
                                    margin: '8px auto',
                                }} />
                            ))}
                        </div>
                    </div>
                )}

                {/* Concepts Card UI Card with buttons BELOW displays */}
                <div
                    style={{
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid #4B5563',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'center',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                        maxWidth: '300px',
                        margin: '32px auto 0 auto',
                    }}
                >
                    <h3 style={{ fontFamily: 'Times New Roman, Times, serif', fontSize: '18px', color: '#4B5563' }}>
                        Concepts
                    </h3>
                    <button
                        style={{
                            backgroundImage: `url(${cardBackgroundImage})`,
                            border: '2px solid #4B5563',
                            borderRadius: '10px',
                            padding: '5px 10px',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '14px',
                            marginTop: '8px',
                            zIndex: 10,
                        }}
                        onClick={handleStartConceptsReading}
                        disabled={conceptsCards.length === 0}
                    >
                        Start Card Reading
                    </button>
                    {showConceptsPicker && (
                        <button
                            style={{
                                backgroundImage: `url(${cardBackgroundImage})`,
                                border: '2px solid #4B5563',
                                borderRadius: '10px',
                                padding: '5px 10px',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '14px',
                                marginTop: '8px',
                                zIndex: 10,
                            }}
                            onClick={handleHideConceptsPicker}
                        >
                            Hide Card Picker
                        </button>
                    )}
                </div>

                {/* Navigation Buttons */}
                <div
                    className="resistance-btn-row"
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '16px',
                        marginTop: '32px',
                    }}>
                    <button
                        className="resistance-btn"
                        style={{
                            backgroundImage: `url(${buttonImage})`,
                            backgroundSize: 'cover',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            width: '120px',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        }}
                        onClick={goBack}
                    >Back</button>
                    <button
                        className="resistance-btn"
                        style={{
                            backgroundImage: `url(${buttonImage})`,
                            backgroundSize: 'cover',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            width: '120px',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        }}
                        onClick={goHome}
                    >Home</button>
                    <button
                        className="resistance-btn"
                        style={{
                            backgroundImage: `url(${buttonImage})`,
                            backgroundSize: 'cover',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            width: '120px',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        }}
                        onClick={handleSave}
                    >Save</button>
                </div>
            </div>
        </>
    );
}

export default Resistance;